import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { ShoppingBag, User, LogOut, Menu, X, Instagram, Facebook, Twitter, MessageCircle, Heart } from "lucide-react";
import { useState } from "react";
import BrandLogo from "@/components/BrandLogo";
import ChatBot from "@/components/ChatBot";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Toaster } from "@/components/ui/sonner";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";

export default function Layout({ children }) {
  const { user, logout, cartCount, wishlistCount } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const links = [
    { to: "/", label: "Home" },
    { to: "/signature", label: "Signature" },
    { to: "/new", label: "New Arrivals" },
    { to: "/men", label: "Men" },
    { to: "/women", label: "Women" },
    { to: "/shirts", label: "Shirts" },
    { to: "/jeans", label: "Jeans" },
    { to: "/oversized", label: "Oversized" },
    { to: "/hoodies", label: "Hoodies" },
    { to: "/sweatshirts", label: "Sweatshirts" },
    { to: "/customize", label: "Customize" },
    { to: "/gifts", label: "Gifts" },
    { to: "/orders", label: "Orders" },
  ];

  return (
    <div className="min-h-screen bg-[#1B1611] text-[#F0E5D0] font-body">
      <div className="bg-[#0A0806] text-[#D4B25C] text-center py-2 text-[10px] tracking-[0.4em] uppercase border-b border-[#D4B25C]/10">
        <Link to="/signature" className="hover:text-[#F0E5D0]">Signature Collection</Link>  ·  <Link to="/new" className="hover:text-[#F0E5D0]">New Arrivals</Link>  ·  Buy 2 Get 1 Free
      </div>

      {/* Header — hamburger left, centered logo, user/bag right */}
      <header className="sticky top-0 z-40 bg-[#1B1611]/85 backdrop-blur-xl border-b border-[#D4B25C]/10">
        <div className="max-w-7xl mx-auto px-6 py-4 grid grid-cols-3 items-center">
          <div className="justify-self-start">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button data-testid="nav-menu-btn" className="p-2 hover:text-[#8B2E2E] transition-colors">
                  <Menu size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#1B1611] text-[#F0E5D0] w-80 border-r border-[#D4B25C]/10">
                <SheetHeader>
                  <SheetTitle className="sr-only">Menu</SheetTitle>
                  <div className="pt-4 pb-6"><BrandLogo dark /></div>
                </SheetHeader>
                <nav className="mt-6 space-y-1">
                  {links.map((l) => (
                    <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)}
                      data-testid={`sheet-nav-${l.label.toLowerCase()}`}
                      className={`block py-3 border-b border-[#D4B25C]/10 font-display text-2xl hover:text-[#D4B25C] transition-colors ${loc.pathname === l.to ? "text-[#D4B25C]" : ""}`}>
                      {l.label}
                    </Link>
                  ))}
                  {user?.role === "admin" && (
                    <Link to="/admin" onClick={() => setMenuOpen(false)} data-testid="sheet-nav-admin"
                      className="block py-3 border-b border-[#D4B25C]/10 font-display text-2xl hover:text-[#D4B25C] transition-colors">
                      Admin
                    </Link>
                  )}
                </nav>
                <div className="mt-10">
                  <p className="label-tag mb-3">Connect</p>
                  <div className="flex gap-4">
                    <a href="https://instagram.com" data-testid="sheet-instagram" className="hover:text-[#D4B25C]"><Instagram size={18} /></a>
                    <a href="https://facebook.com" data-testid="sheet-facebook" className="hover:text-[#D4B25C]"><Facebook size={18} /></a>
                    <a href="https://twitter.com" data-testid="sheet-twitter" className="hover:text-[#D4B25C]"><Twitter size={18} /></a>
                    <a href="https://wa.me/919096213765" data-testid="sheet-whatsapp" className="hover:text-[#D4B25C]"><MessageCircle size={18} /></a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
          <div className="justify-self-center"><BrandLogo dark size="md" /></div>
          <div className="justify-self-end flex items-center gap-2">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-btn" className="p-2 hover:text-[#8B2E2E] transition-colors">
                    <User size={20} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm">
                    <p className="font-semibold truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-orders" onClick={() => nav("/orders")}>My Orders</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-wishlist" onClick={() => nav("/wishlist")}>My Wishlist</DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}>Admin Dashboard</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav("/"); }}>
                    <LogOut size={14} className="mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link data-testid="login-link" to="/login" className="p-2 hover:text-[#8B2E2E] transition-colors">
                <User size={20} />
              </Link>
            )}
            <Link data-testid="wishlist-link" to="/wishlist" className="relative p-2 hover:text-[#8B2E2E] transition-colors">
              <Heart size={20} />
              {wishlistCount > 0 && (
                <span data-testid="wishlist-count" className="absolute -top-0.5 -right-0.5 bg-[#8B2E2E] text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-bold">
                  {wishlistCount}
                </span>
              )}
            </Link>
            <Link data-testid="cart-link" to="/cart" className="relative p-2 hover:text-[#8B2E2E] transition-colors">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-[#8B2E2E] text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>

      <main className="min-h-[calc(100vh-260px)]">{children}</main>

      <footer className="bg-[#0F0B08] text-[#F0E5D0] mt-24 pt-16 pb-8 border-t border-[#D4B25C]/10">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2">
            <BrandLogo dark size="lg" link={false} />
            <p className="text-sm text-white/60 mt-6 max-w-xs">
              Custom t-shirt printing & premium apparel — designed and printed in India.
            </p>
            <div className="flex gap-4 mt-6">
              <a href="https://instagram.com" data-testid="social-instagram" className="hover:text-[#8B2E2E] transition-colors"><Instagram size={20} /></a>
              <a href="https://facebook.com" data-testid="social-facebook" className="hover:text-[#8B2E2E] transition-colors"><Facebook size={20} /></a>
              <a href="https://twitter.com" data-testid="social-twitter" className="hover:text-[#8B2E2E] transition-colors"><Twitter size={20} /></a>
              <a href="https://wa.me/919096213765" data-testid="social-whatsapp" className="hover:text-[#8B2E2E] transition-colors"><MessageCircle size={20} /></a>
            </div>
          </div>
          <div>
            <p className="label-tag text-white/50 mb-4">Shop</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/new" className="hover:text-[#8B2E2E]">New Arrivals</Link></li>
              <li><Link to="/men" className="hover:text-[#8B2E2E]">Men</Link></li>
              <li><Link to="/women" className="hover:text-[#8B2E2E]">Women</Link></li>
              <li><Link to="/shirts" className="hover:text-[#8B2E2E]">Shirts</Link></li>
              <li><Link to="/jeans" className="hover:text-[#8B2E2E]">Jeans</Link></li>
              <li><Link to="/oversized" className="hover:text-[#8B2E2E]">Oversized</Link></li>
              <li><Link to="/hoodies" className="hover:text-[#8B2E2E]">Hoodies</Link></li>
              <li><Link to="/sweatshirts" className="hover:text-[#8B2E2E]">Sweatshirts</Link></li>
              <li><Link to="/customize" className="hover:text-[#8B2E2E]">Customize</Link></li>
              <li><Link to="/gifts" className="hover:text-[#8B2E2E]">Bulk & Merch</Link></li>
            </ul>
          </div>
          <div>
            <p className="label-tag text-white/50 mb-4">Support</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/orders">Track Order</Link></li>
              <li><a href="mailto:venvayoffical@gmail.com">Contact</a></li>
              <li><a href="https://wa.me/919096213765" data-testid="whatsapp-help">WhatsApp Us · +91 90962 13765</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-16 pt-6 border-t border-[#D4B25C]/10 text-xs text-white/40 flex flex-col sm:flex-row justify-between gap-2">
          <p>© 2026 Venvay. All rights reserved.</p>
          <p className="italic">Be Rare. Be <span className="text-[#D4B25C]">Venvay.</span></p>
        </div>
      </footer>
      <Toaster />
      <ChatBot />
    </div>
  );
}
